import { useQuery as useReactQuery, type UseQueryOptions, type QueryKey } from '@tanstack/react-query';

import { FIVE_MINUTES, THIRTY_MINUTES } from './constants';

/**
 * Custom useQuery hook with default configuration for consistent behavior across the app.
 * Sets sensible defaults for refetch behavior and cache times to prevent unnecessary API calls.
 *
 * Default values:
 * - staleTime: 5 minutes (data is considered fresh for 5 minutes)
 * - gcTime: 30 minutes (cache persists for 30 minutes after last use)
 * - refetchOnWindowFocus: false (no refetch when user switches tabs)
 * - refetchOnMount: false (no refetch when component remounts)
 * - refetchOnReconnect: false (no refetch when internet reconnects)
 */
export const useQuery = <
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  return useReactQuery({
    staleTime: FIVE_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...options,
  });
};
